import { useEffect, useState, useRef } from 'react';

/**
 * Debounce a value - only updates after `delay` ms of no changes
 */
export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Debounce a callback function
 */
export function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return (...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  };
}

/**
 * Coalesce rapid function calls - only executes once per interval
 */
export function useCoalescedCallback(callback, interval = 5000) {
  const lastCallRef = useRef(0);
  const pendingRef = useRef(false);
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return (...args) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallRef.current;

    if (timeSinceLastCall >= interval) {
      // Execute immediately
      callbackRef.current(...args);
      lastCallRef.current = now;
      pendingRef.current = false;
    } else {
      // Schedule for later if not already pending
      if (!pendingRef.current) {
        pendingRef.current = true;
        const remaining = interval - timeSinceLastCall;
        
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          callbackRef.current(...args);
          lastCallRef.current = Date.now();
          pendingRef.current = false;
        }, remaining);
      }
    }
  };
}